import { useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useBoardStore } from '../board/boardStore';
import { isTextField } from '../ui/keys';

/**
 * Arrow through the room without leaving the board. Order comes from the
 * masonry store rather than a second query, so "next" means the next tile as
 * laid out, which is the order the eye is following.
 *
 *   ← →                     previous / next item
 *   Esc                     close the panel
 *   Ctrl/Cmd + Backspace    delete, with confirmation
 */
export function useItemNav({
  base,
  itemId,
  enabled,
  onClose,
  onDelete,
}: {
  /** The container's own address — the room, or a section inside it. */
  base: string;
  itemId: string;
  /** False while a dialog or the lightbox owns the keyboard. */
  enabled: boolean;
  onClose: () => void;
  onDelete: () => void;
}): void {
  const navigate = useNavigate();
  const sequence = useBoardStore((state) => state.sequence);

  useEffect(() => {
    if (!enabled) return;

    function onKeyDown(event: KeyboardEvent) {
      if (event.defaultPrevented) return;

      // A field's own Escape blurs it, and this handler only sees the second
      // press — closing the panel out from under a half-typed note would be
      // the same keystroke doing two different things.
      const typing = isTextField(event.target);

      if (event.key === 'Escape') {
        if (typing) return;
        event.preventDefault();
        onClose();
        return;
      }

      // Ctrl+Backspace deletes the previous word inside a field. Taking that
      // over to delete the whole item is not a trade anyone would accept.
      if (event.key === 'Backspace' && (event.ctrlKey || event.metaKey)) {
        if (typing) return;
        event.preventDefault();
        onDelete();
        return;
      }

      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      if (typing) return;

      const index = sequence.indexOf(itemId);
      if (index === -1) return;

      const next = sequence[index + (event.key === 'ArrowLeft' ? -1 : 1)];
      if (next === undefined) return;

      event.preventDefault();
      void navigate(`${base}/${next}`);
    }

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [enabled, sequence, base, itemId, navigate, onClose, onDelete]);
}
